import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import { useSuiteIdentity } from '@/context/SuiteDraftContext/SuiteDraftContext.ts';
import { SUITE_KEYS } from '@/utils/queryKeys.ts';
import type {
  ReleaseDocumentDetail,
  ReleaseFoundation,
  ReleaseHistoryPage,
  ReleaseReadiness,
  SuiteReleaseStatus,
} from '@/types/automationReleases.ts';

const readPolicy = { retry: false, staleTime: 0, refetchOnMount: 'always' as const };

function useReleaseScope(suiteId: string) {
  const { orgId, projectId } = useOrgProject();
  const identity = useSuiteIdentity();
  return SUITE_KEYS.target(orgId, projectId, suiteId, identity);
}

export function useRefreshSuiteReleases(suiteId: string) {
  const client = useQueryClient();
  const scope = useReleaseScope(suiteId);
  return () => client.invalidateQueries({ queryKey: scope });
}

export function useSuiteReleaseStatus(suiteId: string, enabled: boolean) {
  const api = useApiClient();
  const scope = useReleaseScope(suiteId);
  return useQuery({
    queryKey: [...scope, 'release-status'],
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<SuiteReleaseStatus>(
        `/automation-suites/${suiteId}/release-status`,
        { signal },
      );
      if (data.suite_id !== suiteId) throw new Error('Release status belongs to another Suite.');
      return data;
    },
    ...readPolicy,
  });
}

export function useSuiteReleaseHistory(suiteId: string, projectId: string, enabled: boolean) {
  const api = useApiClient();
  const scope = useReleaseScope(suiteId);
  return useInfiniteQuery({
    queryKey: [...scope, 'release-history'],
    enabled,
    initialPageParam: 0,
    queryFn: async ({ signal, pageParam }) => {
      const { data } = await api.get<ReleaseHistoryPage>(
        `/projects/${projectId}/automation-releases`,
        { signal, params: { suite_id: suiteId, offset: pageParam, limit: 50 } },
      );
      if (
        !Array.isArray(data.releases) ||
        data.releases.some((item) => item.suite_id !== suiteId) ||
        (data.next_offset !== null &&
          (!Number.isInteger(data.next_offset) || data.next_offset <= pageParam))
      )
        throw new Error('Release history does not confirm its Suite or pagination.');
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    ...readPolicy,
  });
}

export function useSuiteRelease(suiteId: string, releaseId: string) {
  const api = useApiClient();
  const scope = useReleaseScope(suiteId);
  return useQuery({
    queryKey: [...scope, 'release', releaseId],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ReleaseFoundation>(`/automation-releases/${releaseId}`, {
        signal,
      });
      if (data.id !== releaseId || data.suite_id !== suiteId)
        throw new Error('Release belongs to another target.');
      return data;
    },
    ...readPolicy,
  });
}

export function useReleaseReadiness(suiteId: string, releaseId: string) {
  const api = useApiClient();
  const scope = useReleaseScope(suiteId);
  return useQuery({
    queryKey: [...scope, 'release', releaseId, 'readiness'],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ReleaseReadiness>(
        `/automation-releases/${releaseId}/readiness`,
        { signal },
      );
      if (data.release_id !== releaseId) throw new Error('Evidence belongs to another Release.');
      return data;
    },
    ...readPolicy,
  });
}

export function useReleaseDocuments(suiteId: string, releaseId: string, enabled: boolean) {
  const api = useApiClient();
  const scope = useReleaseScope(suiteId);
  return useQuery({
    queryKey: [...scope, 'release', releaseId, 'documents'],
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ReleaseDocumentDetail>(
        `/automation-releases/${releaseId}/documents`,
        { signal },
      );
      if (data.release_id !== releaseId) throw new Error('Documents belong to another Release.');
      return data;
    },
    ...readPolicy,
  });
}
